/**
 * An accelerator position that reads 0% with the foot off and 100% floored.
 *
 * The standard throttle and pedal PIDs are raw sensor readings, offset by
 * design so a broken wire is distinguishable from a closed throttle. A
 * released pedal therefore commonly reports 15-30%, and a floored one
 * 80-90%. Shown as-is that reads as a stuck throttle, and anything keyed off
 * "foot off" -- coasting, economy scoring -- never triggers.
 *
 * So each source learns its own resting and floored readings as the car is
 * driven, and the driver can pin either end by hand from Setup.
 */
import type { SignalStore } from './store.js';

/**
 * Candidate sources, best first. The pedal is the driver's foot; the
 * throttle plate is what the ECU decided to do with it.
 */
export const ACCELERATOR_SOURCES = ['pedal_rel', 'pedal_d', 'throttle_rel', 'throttle'] as const;

export interface PedalCalibration {
  /** Signal key these readings belong to. */
  source: string;
  /** Lowest reading seen: the released pedal. */
  rest: number;
  /** Highest reading seen. */
  floor: number;
  /** Set by hand in Setup; wins over what was learned. */
  pinnedRest?: number;
  pinnedFull?: number;
}

export interface AcceleratorStatus {
  /** The source in use, if any is reporting. */
  source?: string;
  /** Its latest raw reading, in the PID's own percent. */
  raw?: number;
  /** The reading treated as 0%, and whether that was set by hand. */
  rest?: number;
  restPinned: boolean;
  /** The reading treated as 100%, and whether that was set by hand. */
  full?: number;
  fullPinned: boolean;
  /** Sources abandoned because they never moved while the engine did. */
  rejected: string[];
}

/** Readings within this of rest are rest: the sensor wanders by a step or two. */
const DEADBAND = 0.8;
/** A source this old is not the one being polled any more. */
const STALE_MS = 2000;

class SourceCalibration {
  rest = Infinity;
  floor = -Infinity;
  pinnedRest: number | undefined;
  pinnedFull: number | undefined;
  lastSeen = 0;
  latest: number | undefined;
  private recent: number[] = [];
  private samples = 0;
  private rpmLo = Infinity;
  private rpmHi = -Infinity;

  observe(raw: number, rpm: number | undefined): void {
    this.latest = raw;
    this.recent.push(raw);
    if (this.recent.length > 3) this.recent.shift();
    // Median of three, so a single corrupt frame never becomes the calibration.
    if (this.recent.length === 3) {
      const [a, b, c] = this.recent as [number, number, number];
      const med = Math.max(Math.min(a, b), Math.min(Math.max(a, b), c));
      if (med < this.rest) this.rest = med;
      if (med > this.floor) this.floor = med;
    }
    this.samples++;
    if (rpm !== undefined) {
      if (rpm < this.rpmLo) this.rpmLo = rpm;
      if (rpm > this.rpmHi) this.rpmHi = rpm;
    }
  }

  /**
   * A source that has not moved while the engine went through 1500 rpm of
   * range is not connected to the driver's foot. Some ECUs report a pedal PID
   * that never changes, and many diesels report an intake flap as "throttle".
   */
  get stuck(): boolean {
    return this.samples >= 100 && this.rpmHi - this.rpmLo >= 1500 && this.floor - this.rest < 2.5;
  }

  get restPoint(): number | undefined {
    const rest = this.pinnedRest ?? this.rest;
    return Number.isFinite(rest) ? rest : undefined;
  }

  /**
   * The reading treated as 100%.
   *
   * Until the pedal has been floored the true top is unknown. Sensors are
   * usually offset symmetrically -- 15% at rest, 85% floored -- so that is the
   * assumption, and any higher reading actually seen replaces it.
   */
  fullPoint(rest: number): number {
    if (this.pinnedFull !== undefined && this.pinnedFull > rest + 5) return this.pinnedFull;
    const assumed = Math.min(100, Math.max(100 - rest, rest + 30));
    return Math.max(this.floor, assumed);
  }

  normalise(raw: number): number | undefined {
    const rest = this.restPoint;
    if (rest === undefined || this.recent.length < 3) return undefined;
    if (raw - rest <= DEADBAND) return 0;
    const full = this.fullPoint(rest);
    return Math.max(0, Math.min(100, ((raw - rest) / (full - rest)) * 100));
  }
}

export class AcceleratorCalibrator {
  private readonly sources = new Map<string, SourceCalibration>();
  private readonly rejected = new Set<string>();
  private active: string | undefined;
  /** Bumped whenever the list of usable sources changes. */
  private _version = 0;

  get version(): number {
    return this._version;
  }

  /** Sources still worth polling, best first. */
  candidates(): string[] {
    return ACCELERATOR_SOURCES.filter((k) => !this.rejected.has(k));
  }

  /** Read the best fresh source from the store and return 0-100, if possible. */
  update(store: SignalStore, rpm: number | undefined, now: number): number | undefined {
    for (const key of this.candidates()) {
      const state = store.get(key);
      if (!state || state.value === undefined || now - state.stats.lastUpdate > STALE_MS) continue;
      const cal = this.calibration(key);
      if (state.stats.lastUpdate !== cal.lastSeen) {
        cal.lastSeen = state.stats.lastUpdate;
        cal.observe(state.value, rpm);
        if (cal.stuck) {
          this.rejected.add(key);
          this._version++;
          continue;
        }
      }
      this.active = key;
      return cal.normalise(state.value);
    }
    this.active = undefined;
    return undefined;
  }

  status(): AcceleratorStatus {
    const cal = this.active ? this.sources.get(this.active) : undefined;
    const rest = cal?.restPoint;
    return {
      source: this.active,
      raw: cal?.latest,
      rest,
      restPinned: cal?.pinnedRest !== undefined,
      full: cal && rest !== undefined ? cal.fullPoint(rest) : undefined,
      fullPinned: cal?.pinnedFull !== undefined,
      rejected: [...this.rejected],
    };
  }

  /**
   * Take the active source's current reading as released (0%) or floored
   * (100%). Returns false when there is nothing to take it from.
   */
  pin(end: 'rest' | 'full'): boolean {
    const cal = this.active ? this.sources.get(this.active) : undefined;
    if (!cal || cal.latest === undefined) return false;
    if (end === 'rest') {
      cal.pinnedRest = cal.latest;
      if (cal.latest < cal.rest) cal.rest = cal.latest;
    } else {
      cal.pinnedFull = cal.latest;
    }
    return true;
  }

  export(): PedalCalibration[] {
    const out: PedalCalibration[] = [];
    for (const [source, cal] of this.sources) {
      if (!Number.isFinite(cal.rest) && cal.pinnedRest === undefined) continue;
      const entry: PedalCalibration = {
        source,
        rest: Number.isFinite(cal.rest) ? cal.rest : (cal.pinnedRest as number),
        floor: Number.isFinite(cal.floor) ? cal.floor : (cal.pinnedRest as number),
      };
      if (cal.pinnedRest !== undefined) entry.pinnedRest = cal.pinnedRest;
      if (cal.pinnedFull !== undefined) entry.pinnedFull = cal.pinnedFull;
      out.push(entry);
    }
    return out;
  }

  import(list: readonly PedalCalibration[] | undefined): void {
    this.sources.clear();
    // Anything may arrive here from a file, so nothing about its shape is assumed.
    for (const p of Array.isArray(list) ? list : []) {
      if (typeof p !== 'object' || p === null) continue;
      if (!(ACCELERATOR_SOURCES as readonly string[]).includes(p.source)) continue;
      if (!Number.isFinite(p.rest) || !Number.isFinite(p.floor)) continue;
      const cal = this.calibration(p.source);
      cal.rest = p.rest;
      cal.floor = Math.max(p.floor, p.rest);
      if (typeof p.pinnedRest === 'number' && Number.isFinite(p.pinnedRest)) cal.pinnedRest = p.pinnedRest;
      if (typeof p.pinnedFull === 'number' && Number.isFinite(p.pinnedFull)) cal.pinnedFull = p.pinnedFull;
    }
  }

  reset(): void {
    this.sources.clear();
    this.active = undefined;
    if (this.rejected.size > 0) {
      this.rejected.clear();
      this._version++;
    }
  }

  private calibration(key: string): SourceCalibration {
    let cal = this.sources.get(key);
    if (!cal) {
      cal = new SourceCalibration();
      this.sources.set(key, cal);
    }
    return cal;
  }
}
